import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { SERVICES, CONTACT_INFO } from '../data';
import { getResizedImageUrl } from '../lib/utils';

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [galleryData, setGalleryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null);

  // Find static service configuration
  const serviceConfig = SERVICES.find(s => s.slug === slug);

  useEffect(() => {
    if (!serviceConfig) {
      // If service slug is invalid, redirect to 404
      navigate('/404', { replace: true });
      return;
    }

    setLoading(true);

    // Fetch page content and gallery content in parallel
    const fetchPage = fetch(`https://api.dev.auxo.dk/public/v2/content/sider?page_slug=${slug}`)
      .then(res => res.json())
      .then(data => data.items?.[0] || null);

    const fetchGallery = fetch(`https://api.dev.auxo.dk/public/v2/content/galleri?gallery_slug=${slug}`)
      .then(res => res.json())
      .then(data => data.items?.[0] || null);

    Promise.all([fetchPage, fetchGallery])
      .then(([page, gallery]) => {
        setPageData(page);
        setGalleryData(gallery);
        setLoading(false);
      })
      .catch(err => {
        console.error('Fejl ved hentning af ydelsesdata:', err);
        setLoading(false);
      });
  }, [slug, serviceConfig, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-secondary-green" />
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-16 text-center">
        <h2 className="font-display text-xl font-bold text-primary-green">Ydelsen blev ikke fundet</h2>
        <Link to="/" className="mt-4 text-xs font-bold text-secondary-green uppercase tracking-wider hover:underline">
          Gå til forsiden
        </Link>
      </div>
    );
  }

  const title = pageData.title?.da || serviceConfig.title;
  const contentHtml = pageData.content?.da || '';
  const bannerImg = pageData.banner_image?.[0] || 'https://assets.auxodev.dk/assets/340cd821-77c4-4f44-a38b-9945cfcac15b/hero-danish-garden.jpg';
  const galleryImages = galleryData?.images || [];

  return (
    <div className="w-full">
      {/* Page Banner */}
      <section className="relative h-[40vh] min-h-[250px] w-full overflow-hidden bg-primary-green">
        <img 
          src={getResizedImageUrl(bannerImg, 'width=1600,quality=80,format=auto')} 
          alt={title} 
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-green via-primary-green/50 to-transparent" />
        {/* Decorative Background Leaves */}
        <div className="absolute inset-0 pointer-events-none opacity-10 flex justify-between items-center px-12 overflow-hidden z-10">
          <i className="fa-solid fa-leaf text-white text-5xl animate-float-leaf-1 transform translate-y-12" />
          <i className="fa-solid fa-leaf text-white text-4xl animate-float-leaf-2 transform -translate-y-12 hidden md:block" />
          <i className="fa-solid fa-leaf text-white text-6xl animate-float-leaf-3 transform translate-y-6 hidden lg:block" />
        </div>
        <div className="relative z-20 mx-auto flex h-full max-w-7xl flex-col justify-end px-4 pb-12 sm:px-6 lg:px-8 text-white">
          <span className="text-xs font-bold uppercase tracking-widest text-secondary-green block mb-2">
            Vores ydelser
          </span>
          <h1 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl uppercase">
            {title}
          </h1>
        </div>
      </section>

      {/* Content & Benefits Section */}
      <section className="pt-[30px] pb-16 md:pt-[30px] md:pb-24 lg:pt-24 lg:pb-24 bg-white relative overflow-hidden">
        {/* Decorative Background Leaves */}
        <div className="absolute inset-0 pointer-events-none opacity-5 flex justify-between items-center px-12 overflow-hidden">
          <i className="fa-solid fa-leaf text-primary-green text-5xl animate-float-leaf-1 transform translate-y-12" />
          <i className="fa-solid fa-leaf text-primary-green text-4xl animate-float-leaf-2 transform -translate-y-12 hidden md:block" />
          <i className="fa-solid fa-leaf text-primary-green text-6xl animate-float-leaf-3 transform translate-y-6 hidden lg:block" />
        </div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            {/* Left/Middle Columns: CMS Content */}
            <div className="lg:col-span-2 space-y-6">
              <div 
                className="prose prose-neutral max-w-none text-sm text-neutral-muted leading-relaxed space-y-4
                  prose-headings:font-display prose-headings:font-bold prose-headings:text-primary-green prose-headings:uppercase
                  prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            </div>

            {/* Right Column: Benefits Box */}
            <div className="bg-neutral-light-bg p-8 rounded-sm border border-gray-100 shadow-xs space-y-6">
              <h3 className="font-display text-sm font-bold uppercase tracking-wider text-primary-green border-b border-gray-200 pb-3">
                Fordele ved {title}
              </h3>
              <ul className="space-y-3">
                {serviceConfig.benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-xs text-neutral-muted leading-relaxed">
                    <i className="fa-solid fa-circle-check text-secondary-green mt-0.5 shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
              <div className="pt-4">
                <Link 
                  to="/kontakt" 
                  className="w-full text-center bg-accent-orange hover:bg-accent-orange-dark text-white font-display text-xs font-bold px-6 py-3.5 uppercase tracking-wider transition-all duration-200 rounded-sm shadow-sm block"
                >
                  Få et tilbud nu
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="py-16 md:py-24 bg-neutral-light-bg border-t border-gray-100 relative overflow-hidden">
          {/* Decorative Background Leaves */}
          <div className="absolute inset-0 pointer-events-none opacity-5 flex justify-between items-center px-12 overflow-hidden">
            <i className="fa-solid fa-leaf text-primary-green text-5xl animate-float-leaf-1 transform translate-y-12" />
            <i className="fa-solid fa-leaf text-primary-green text-4xl animate-float-leaf-2 transform -translate-y-12 hidden md:block" />
            <i className="fa-solid fa-leaf text-primary-green text-6xl animate-float-leaf-3 transform translate-y-6 hidden lg:block" />
          </div>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
              <span className="text-xs font-bold uppercase tracking-widest text-secondary-green block">
                Billedgalleri
              </span>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-primary-green">
                Se vores udførte arbejde
              </h2>
              <p className="font-body text-sm text-neutral-muted leading-relaxed">
                Her er et udvalg af billeder fra vores projekter inden for {title.toLowerCase()}.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryImages.map((imgUrl, idx) => (
                <div 
                  key={idx}
                  onClick={() => setActiveImage(imgUrl)}
                  className="group relative h-64 overflow-hidden rounded-sm bg-gray-100 shadow-xs cursor-pointer transition-all duration-300 hover:shadow-md"
                >
                  <img 
                    src={getResizedImageUrl(imgUrl, 'width=600,height=400,fit=cover,quality=80,format=auto')} 
                    alt={`${title} reference ${idx + 1}`} 
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                    <i className="fa-solid fa-magnifying-glass text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Call To Action Banner */}
      <section className="bg-primary-green text-white py-12 md:py-16 border-t border-primary-green-light relative overflow-hidden">
        {/* Decorative Background Leaves */}
        <div className="absolute inset-0 pointer-events-none opacity-10 flex justify-between items-center px-12 overflow-hidden z-10">
          <i className="fa-solid fa-leaf text-white text-5xl animate-float-leaf-1 transform translate-y-12" />
          <i className="fa-solid fa-leaf text-white text-4xl animate-float-leaf-2 transform -translate-y-12 hidden md:block" />
          <i className="fa-solid fa-leaf text-white text-6xl animate-float-leaf-3 transform translate-y-6 hidden lg:block" />
        </div>
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center space-y-4 relative z-20">
          <h3 className="font-display text-xl md:text-2xl font-bold uppercase tracking-wider text-white">
            Har du brug for hjælp til {title.toLowerCase()}?
          </h3>
          <p className="font-body text-xs text-gray-300 max-w-xl mx-auto leading-relaxed">
            Kontakt os i dag for en uforpligtende snak om dine haveønsker. Vi giver altid et stærkt og fair tilbud.
          </p>
          <div className="pt-4 flex flex-wrap justify-center gap-4">
            <Link 
              to="/kontakt" 
              className="bg-accent-orange hover:bg-accent-orange-dark text-white font-display text-xs font-bold px-8 py-3.5 uppercase tracking-wider transition-all duration-200 rounded-sm shadow-sm"
            >
              Kontakt os
            </Link>
            <a 
              href={`tel:${CONTACT_INFO.phoneRaw}`} 
              className="bg-white/10 hover:bg-white/20 text-white border border-white/30 font-display text-xs font-bold px-8 py-3.5 uppercase tracking-wider transition-all duration-200 rounded-sm whitespace-nowrap"
            >
              Ring op: {CONTACT_INFO.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 animate-in fade-in duration-200"
          onClick={() => setActiveImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white text-3xl cursor-pointer hover:text-secondary-green transition-colors"
            onClick={() => setActiveImage(null)}
          >
            <i className="fa-solid fa-xmark" />
          </button>
          <img 
            src={activeImage} 
            alt="Udført arbejde i stor visning" 
            className="max-h-[90vh] max-w-full object-contain rounded-sm shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}
